import { mixed, Schema, string } from "yup";
import { VoIpProt, SIP, Gk, GkAuth } from "./types";
import {
  Enum_BfcpTransportProtocol,
  Enum_GateKeeperEnable,
  Enum_SipConfigType,
  Enum_SipRegistrarServerType,
  Enum_SipTransportProtocol
} from "generated/graphql";
import i18next from "utilities/i18n";
import {
  boolean,
  stringF,
  isValidIpOrDomain,
  whenDisabled,
  object
} from "./helpers";

const gkAuth = object<GkAuth>({
  enable: boolean("ENABLE_H323_GK_AUTH"),
  password: stringF("H323_GK_AUTH_PASSWORD").when("enable", {
    is: true,
    then: string()
      .trim()
      .max(47)
  }),
  userId: stringF("H323_GK_AUTH_USER_ID").when("enable", {
    is: true,
    then: string()
      .trim()
      .max(62)
  })
});
const gk = object<Gk>({
  ipAddress: stringF("H323_GK_IP_ADDRESS").when("mode", {
    is: Enum_GateKeeperEnable.Specify,
    then: string().test(
      "validate-domain",
      i18next.t("ERROR_H323_GK_IP_ADDRESS"),
      isValidIpOrDomain
    )
  }),
  mode: mixed()
    .label("H323_GATEKEEPER")
    .default(Enum_GateKeeperEnable.Auto)
    .oneOf([...Object.values(Enum_GateKeeperEnable)])
    .required(),
  auth: gkAuth.when(
    "mode",
    (mode: Enum_GateKeeperEnable, schema: Schema<any>) => {
      switch (mode) {
        case Enum_GateKeeperEnable.Auto:
        case Enum_GateKeeperEnable.Off:
          return object();
        case Enum_GateKeeperEnable.Specify:
        default:
          return schema;
      }
    }
  )
});

export const H323 = object({
  enable: boolean("ENABLE_H323"),
  e164: stringF("H323_E164").when("enable", {
    is: true,
    then: string()
      .trim()
      .test("isNumeric", i18next.t("ERROR_H323_E164"), v => !isNaN(v))
      .max(15)
  }),
  name: stringF("H323_NAME").when("enable", {
    is: true,
    then: string()
      .trim()
      .max(36)
  }),
  gk: gk.when("enable", {
    is: false,
    then: object()
  })
});
export function toggleEnum(
  values: object,
  label: string,
  then: Schema<any> = mixed()
) {
  return mixed()
    .label(label)
    .when("enable", {
      is: true,
      then: then.oneOf(Object.values(values))
    });
}

export const sipSchema = object<SIP>({
  enable: boolean("ENABLE_SIP"),
  auth: object({
    password: stringF("PASSWORD")
      .trim()
      .max(47),
    userId: stringF("USERNAME")
      .trim()
      .max(369)
  }).when("enable", whenDisabled(object())),
  serverType: toggleEnum(
    Enum_SipConfigType,
    "SIP_SERVER_CONFIGURATION",
    mixed().required()
  ).default(Enum_SipConfigType.Auto),
  transport: toggleEnum(
    Enum_SipTransportProtocol,
    "TRANSPORT_PROTOCOL",
    mixed().when("serverType", {
      is: Enum_SipConfigType.Specify,
      then: mixed().required()
    })
  ).default(Enum_SipTransportProtocol.Auto),
  registrarServer: stringF("REGISTRAR_SERVER").when("enable", {
    is: true,
    then: string().when("serverType", {
      is: Enum_SipConfigType.Specify,
      then: string()
        .max(253)
        .test(
          "validate-domain",
          i18next.t("ERROR_SIP_TRANSPORT"),
          isValidIpOrDomain
        )
    })
  }),
  proxyServer: stringF("PROXY_SERVER").when("enable", {
    is: true,
    then: string().when("serverType", {
      is: Enum_SipConfigType.Specify,
      then: string()
        .max(253)
        .test(
          "validate-domain",
          i18next.t("ERROR_SIP_TRANSPORT"),
          isValidIpOrDomain
        )
    })
  }),
  bfcpTransportPreference: toggleEnum(
    Enum_BfcpTransportProtocol,
    "BFCP_TRANSPORT_PREFERENCE"
  ).default(Enum_BfcpTransportProtocol.PreferTcp),
  forceConnectionReuse: boolean("FORCE_CONNECTION_REUSE"),
  registrarServerType: toggleEnum(
    Enum_SipRegistrarServerType,
    "REGISTRAR_SERVER_TYPE"
  ).default(Enum_SipRegistrarServerType.Microsoft),
  userName: stringF("SIGN_IN_ADDRESS").when("enable", {
    is: true,
    then: string()
      .trim()
      .max(369)
  })
});
export const voIpProt = object<VoIpProt>({
  H323,
  SIP: sipSchema
});
